/**
 * NHL share cards — pure client-side canvas graphics (1080-wide), no external
 * assets. Mirrors the NFL share.js loop: a shared exporter (shareCanvas) plus
 * draw* builders and fetch+render shareNhl* entry points. Every card carries the
 * compliance footer (research, not advice · 21+).
 */
import * as api from './api'
import { MARKET_LABEL, bookName, RATING_LETTER } from './ui'

const C = {
  bg: '#0A0E14', panel: '#111827', border: '#1E2A3A', line: '#243146',
  text: '#F1F5F9', muted: '#93A4B8', green: '#00D97E', amber: '#F59E0B',
  red: '#EF4444', ice: '#38BDF8', mono: "'SF Mono','Menlo',monospace",
}
const TIER_C = { ELITE: '#3B82F6', 'BEST BET': '#3B82F6', STRONG: C.green,
  PLAY: C.amber, WATCH: C.amber, PASS: C.red }
const TIER_LABEL = { ELITE: 'ELITE', 'BEST BET': 'TOP RATED', STRONG: 'STRONG',
  PLAY: 'MODERATE', WATCH: 'WATCH', PASS: 'LOW' }

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function base(w, h) {
  const cv = document.createElement('canvas')
  cv.width = w; cv.height = h
  const ctx = cv.getContext('2d')
  const g = ctx.createLinearGradient(0, 0, 0, h)
  g.addColorStop(0, '#0E1A14'); g.addColorStop(0.5, C.bg); g.addColorStop(1, '#080B10')
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h)
  return { cv, ctx }
}

function header(ctx, w, sub) {
  ctx.textAlign = 'left'
  ctx.font = `900 46px system-ui`
  ctx.fillStyle = C.text; ctx.fillText('LINE', 60, 84)
  const lw = ctx.measureText('LINE').width
  ctx.fillStyle = C.green; ctx.fillText('BRKR', 60 + lw, 84)
  ctx.fillStyle = C.ice; ctx.font = `800 26px system-ui`
  ctx.fillText('🏒 NHL', 60, 122)
  if (sub) { ctx.fillStyle = C.muted; ctx.font = `600 24px system-ui`
    ctx.textAlign = 'right'; ctx.fillText(sub, w - 60, 100); ctx.textAlign = 'left' }
}

function footer(ctx, w, h) {
  ctx.textAlign = 'center'; ctx.fillStyle = C.muted; ctx.font = `600 20px system-ui`
  ctx.fillText('research, not advice · 21+ · linebrkr', w / 2, h - 34)
  ctx.textAlign = 'left'
}

function tierPill(ctx, x, y, tier) {
  const c = TIER_C[tier] || C.amber
  const label = TIER_LABEL[tier] || tier
  ctx.font = `800 22px system-ui`
  const tw = ctx.measureText(label).width + 34
  ctx.fillStyle = c + '22'; roundRect(ctx, x, y, tw, 40, 20); ctx.fill()
  ctx.strokeStyle = c + '99'; ctx.lineWidth = 2; roundRect(ctx, x, y, tw, 40, 20); ctx.stroke()
  ctx.fillStyle = c; ctx.textAlign = 'left'; ctx.fillText(label, x + 17, y + 28)
  return tw
}

const pct = v => `${Math.round((v || 0) * 100)}%`
const goalMk = p => p.market === 'goals'
const lineTxt = p => goalMk(p) ? 'Anytime Goal'
  : `Over ${p.book_line != null ? p.book_line : p.model_line}${p.book_line == null ? ' (proj)' : ''}`

// ── single play card ─────────────────────────────────────────────────────────
export function drawNhlPick(p) {
  const { cv, ctx } = base(1080, 1080)
  header(ctx, 1080, new Date().toLocaleDateString())
  roundRect(ctx, 60, 190, 960, 620, 28); ctx.fillStyle = C.panel; ctx.fill()
  ctx.strokeStyle = C.border; ctx.lineWidth = 2; roundRect(ctx, 60, 190, 960, 620, 28); ctx.stroke()

  ctx.fillStyle = C.text; ctx.font = `900 62px system-ui`; ctx.textAlign = 'left'
  ctx.fillText(p.name || '', 100, 300)
  ctx.fillStyle = C.muted; ctx.font = `600 30px system-ui`
  ctx.fillText(`${p.team} vs ${p.opponent} · ${MARKET_LABEL[p.market] || p.market}`, 100, 350)

  tierPill(ctx, 100, 390, p.tier)

  ctx.fillStyle = C.text; ctx.font = `800 54px ${C.mono}`
  ctx.fillText(lineTxt(p), 100, 520)

  // big hit %
  ctx.textAlign = 'right'
  const prob = p.prob_over || 0
  ctx.fillStyle = prob >= 0.6 ? C.green : prob >= 0.5 ? C.ice : C.amber
  ctx.font = `900 150px ${C.mono}`; ctx.fillText(pct(prob), 980, 560)
  ctx.fillStyle = C.muted; ctx.font = `700 26px system-ui`
  ctx.fillText(p.book_line != null ? 'HIT PROBABILITY' : 'MODEL PROBABILITY', 980, 600)

  ctx.textAlign = 'left'
  if (p.edge_pct != null && !p.line_suspect) {
    ctx.fillStyle = p.edge_pct >= 0 ? C.green : C.red; ctx.font = `800 34px system-ui`
    ctx.fillText(`${p.edge_pct >= 0 ? '+' : ''}${p.edge_pct}pp edge vs book`, 100, 650)
  }
  if (p.book != null && p.over_odds != null) {
    ctx.fillStyle = C.muted; ctx.font = `600 28px system-ui`
    ctx.fillText(`best ${p.over_odds > 0 ? '+' : ''}${p.over_odds} · ${bookName(p.book)}`, 100, 710)
  }
  footer(ctx, 1080, 1080)
  return cv
}

// ── top plays board ──────────────────────────────────────────────────────────
export function drawNhlTop(plays, market) {
  const rows = plays.slice(0, 8)
  const h = 320 + rows.length * 96 + 90
  const { cv, ctx } = base(1080, h)
  header(ctx, 1080, new Date().toLocaleDateString())
  ctx.fillStyle = C.text; ctx.font = `900 52px system-ui`; ctx.textAlign = 'left'
  ctx.fillText(`Top ${MARKET_LABEL[market] || market} plays`, 60, 210)

  let y = 280
  rows.forEach((p, i) => {
    roundRect(ctx, 60, y, 960, 84, 16); ctx.fillStyle = C.panel; ctx.fill()
    ctx.fillStyle = C.muted; ctx.font = `800 30px ${C.mono}`; ctx.textAlign = 'left'
    ctx.fillText(`${i + 1}`, 84, y + 54)
    ctx.fillStyle = C.text; ctx.font = `800 34px system-ui`
    ctx.fillText(p.name || '', 150, y + 44)
    ctx.fillStyle = C.muted; ctx.font = `600 24px system-ui`
    ctx.fillText(`${p.team} vs ${p.opponent} · ${lineTxt(p)}`, 150, y + 74)
    const prob = p.prob_over || 0
    ctx.textAlign = 'right'
    ctx.fillStyle = prob >= 0.6 ? C.green : C.ice; ctx.font = `900 44px ${C.mono}`
    ctx.fillText(pct(prob), 1000, y + 56)
    ctx.textAlign = 'left'
    y += 96
  })
  footer(ctx, 1080, h)
  return cv
}

// ── record / proof card ──────────────────────────────────────────────────────
export function drawNhlProof(scorecard, tiers) {
  const { cv, ctx } = base(1080, 1080)
  header(ctx, 1080, 'track record')
  const rec = scorecard.recommended || {}
  const real = scorecard.realized || {}
  ctx.fillStyle = C.text; ctx.font = `900 52px system-ui`; ctx.textAlign = 'left'
  ctx.fillText('The Record', 60, 210)

  const wl = r => `${r.wins || 0}-${r.losses || 0}${r.pushes ? `-${r.pushes}` : ''}`
  const tile = (x, label, val, col) => {
    roundRect(ctx, x, 260, 290, 180, 20); ctx.fillStyle = C.panel; ctx.fill()
    ctx.textAlign = 'center'; ctx.fillStyle = col; ctx.font = `900 60px ${C.mono}`
    ctx.fillText(val, x + 145, 350)
    ctx.fillStyle = C.muted; ctx.font = `700 22px system-ui`; ctx.fillText(label, x + 145, 400)
    ctx.textAlign = 'left'
  }
  tile(60, 'RECOMMENDED', wl(rec), C.green)
  tile(395, 'WIN %', rec.win_pct != null ? pct(rec.win_pct) : '—', C.text)
  tile(730, 'REALIZED (BOOK)', wl(real), (real.win_pct || 0) >= 0.5 ? C.green : C.amber)

  let y = 520
  ctx.fillStyle = C.muted; ctx.font = `800 26px system-ui`; ctx.fillText('BY TIER', 60, y); y += 20
  Object.entries(tiers || {}).forEach(([t, b]) => {
    y += 60
    ctx.fillStyle = TIER_C[t] || C.text; ctx.font = `800 32px system-ui`; ctx.textAlign = 'left'
    ctx.fillText(TIER_LABEL[t] || t, 60, y)
    ctx.fillStyle = C.text; ctx.font = `700 32px ${C.mono}`; ctx.textAlign = 'right'
    ctx.fillText(`${b.wins}-${b.losses}${b.win_pct != null ? ' · ' + pct(b.win_pct) : ''}`, 1020, y)
    ctx.textAlign = 'left'
  })
  ctx.fillStyle = C.muted; ctx.font = `600 22px system-ui`; ctx.textAlign = 'left'
  ctx.fillText('graded at the real closing book line · linebrkr.app/nhl/proof', 60, 980)
  footer(ctx, 1080, 1080)
  return cv
}


// ── ranked board (the NFL "Share a board" family) ────────────────────────────
// One row per play: rank · grade · player · the bet + best price · headline % and
// edge. Every number is the board's own (prob_over / edge_pct / over_odds); a row
// with no posted price says "proj line" instead of showing a price.
const RATE_C = r => r && r[0] === 'A' ? C.green : r && r[0] === 'B' ? C.amber : C.muted
const betTxt = p => goalMk(p) ? 'Anytime goal'
  : `Over ${p.book_line != null ? p.book_line : p.model_line} ${MARKET_LABEL[p.market] || p.market}`
const fmtO = o => o == null ? '' : (o > 0 ? `+${o}` : `${o}`)

export function drawNhlBoard(title, sub, plays, footNote) {
  const rows = plays.slice(0, 10)
  const h = 300 + rows.length * 104 + 130
  const { cv, ctx } = base(1080, h)
  header(ctx, 1080, sub)
  ctx.fillStyle = C.text; ctx.font = `900 54px system-ui`; ctx.textAlign = 'left'
  ctx.fillText(title, 60, 220)
  let y = 260
  rows.forEach((p, i) => {
    roundRect(ctx, 60, y, 960, 92, 18); ctx.fillStyle = C.panel; ctx.fill()
    ctx.fillStyle = C.muted; ctx.font = `800 28px ${C.mono}`; ctx.fillText(`${i + 1}`, 84, y + 56)
    const tier = p.book_line != null ? p.tier : (p.conviction || p.tier)
    const letter = RATING_LETTER[tier] || 'C'
    const rc = RATE_C(letter)
    ctx.strokeStyle = rc; ctx.lineWidth = 3; roundRect(ctx, 128, y + 20, 64, 52, 12); ctx.stroke()
    ctx.fillStyle = rc; ctx.font = `900 28px ${C.mono}`; ctx.textAlign = 'center'
    ctx.fillText(letter, 160, y + 56); ctx.textAlign = 'left'
    ctx.fillStyle = C.text; ctx.font = `800 33px system-ui`
    ctx.fillText(p.name || '', 214, y + 44)
    const price = p.book_line != null && p.over_odds != null ? ` · ${fmtO(p.over_odds)} ${bookName(p.book)}` : ' · proj line'
    ctx.fillStyle = C.muted; ctx.font = `600 23px system-ui`
    ctx.fillText(`${p.team} vs ${p.opponent} · ${betTxt(p)}${price}`, 214, y + 76)
    const prob = p.prob_over || 0
    ctx.textAlign = 'right'; ctx.font = `900 44px ${C.mono}`
    ctx.fillStyle = goalMk(p) ? (prob >= 0.32 ? C.green : C.ice) : (prob >= 0.6 ? C.green : prob >= 0.52 ? C.ice : C.amber)
    ctx.fillText(pct(prob), 1000, y + 52)
    if (p.edge_pct != null && !p.line_suspect) {
      ctx.font = `800 21px system-ui`; ctx.fillStyle = p.edge_pct >= 0 ? C.green : C.red
      ctx.fillText(`${p.edge_pct >= 0 ? '+' : ''}${p.edge_pct}pp edge`, 1000, y + 80)
    }
    ctx.textAlign = 'left'
    y += 104
  })
  if (footNote) { ctx.fillStyle = C.muted; ctx.font = `600 21px system-ui`; ctx.fillText(footNote, 60, y + 30) }
  footer(ctx, 1080, h)
  return cv
}

// ── matchup spots ─────────────────────────────────────────────────────────────
export function drawNhlSpots(sig, sub) {
  const items = [
    ...(sig.shootouts || []).map(g => ['🔥', g.game, `Game total ${g.total} — pace up, skater overs`]),
    ...(sig.weak_pk || []).map(w => ['⚡', `${w.team} vs ${w.opp}`, `${w.opp} PK ${w.opp_pk_pct}% — power-play points up`]),
    ...(sig.goalie_spots || []).map(g => ['🥅', `${g.goalie || g.team} (${g.team})`, `vs ${g.opp} — ${g.opp_shots_pg} shots/g faced`]),
    ...(sig.back_to_backs || []).map(t => ['💤', t, '2nd of a back-to-back — fatigue spot']),
  ].slice(0, 10)
  const h = 300 + items.length * 104 + 110
  const { cv, ctx } = base(1080, h)
  header(ctx, 1080, sub)
  ctx.fillStyle = C.text; ctx.font = `900 54px system-ui`; ctx.textAlign = 'left'
  ctx.fillText('Matchup Spots', 60, 220)
  let y = 260
  items.forEach(([icon, title, line]) => {
    roundRect(ctx, 60, y, 960, 92, 18); ctx.fillStyle = C.panel; ctx.fill()
    ctx.font = `40px system-ui`; ctx.fillText(icon, 84, y + 60)
    ctx.fillStyle = C.text; ctx.font = `800 33px system-ui`; ctx.fillText(title, 150, y + 44)
    ctx.fillStyle = C.muted; ctx.font = `600 23px system-ui`; ctx.fillText(line, 150, y + 76)
    y += 104
  })
  footer(ctx, 1080, h)
  return cv
}

// ── recap (graded) ────────────────────────────────────────────────────────────
export function drawNhlRecap(card, record, sub) {
  const rows = card.slice(0, 10)
  const h = 380 + rows.length * 96 + 110
  const { cv, ctx } = base(1080, h)
  header(ctx, 1080, sub)
  const w = rows.filter(r => r.result === 'win').length, l = rows.filter(r => r.result === 'loss').length
  ctx.fillStyle = C.text; ctx.font = `900 54px system-ui`; ctx.textAlign = 'left'
  ctx.fillText('Results', 60, 220)
  ctx.fillStyle = w >= l ? C.green : C.amber; ctx.font = `900 54px ${C.mono}`; ctx.textAlign = 'right'
  ctx.fillText(`${w}-${l}`, 1020, 220); ctx.textAlign = 'left'
  if (record && (record.wins || record.losses)) {
    ctx.fillStyle = C.muted; ctx.font = `700 24px system-ui`
    ctx.fillText(`Season: ${record.wins}-${record.losses}${record.win_pct != null ? ` (${pct(record.win_pct)})` : ''} · graded at the book line`, 60, 270)
  }
  let y = 310
  rows.forEach(r => {
    const win = r.result === 'win'
    roundRect(ctx, 60, y, 960, 84, 16); ctx.fillStyle = C.panel; ctx.fill()
    ctx.fillStyle = win ? C.green : r.result === 'loss' ? C.red : C.muted
    ctx.font = `900 34px system-ui`; ctx.fillText(win ? '✓' : r.result === 'loss' ? '✕' : '•', 86, y + 54)
    ctx.fillStyle = C.text; ctx.font = `800 31px system-ui`; ctx.fillText(r.name || '', 140, y + 40)
    const ln = r.book_line != null ? r.book_line : r.line
    ctx.fillStyle = C.muted; ctx.font = `600 23px system-ui`
    ctx.fillText(`${r.team} · ${r.market === 'goals' ? 'Anytime goal' : `Over ${ln} ${MARKET_LABEL[r.market] || r.market}`}`, 140, y + 72)
    ctx.textAlign = 'right'; ctx.fillStyle = win ? C.green : C.red; ctx.font = `900 38px ${C.mono}`
    ctx.fillText(`${r.actual ?? '—'}`, 1000, y + 54); ctx.textAlign = 'left'
    y += 96
  })
  footer(ctx, 1080, h)
  return cv
}

// ── exporter ─────────────────────────────────────────────────────────────────
export function shareCanvas(cv, filename, text) {
  return new Promise(resolve => {
    cv.toBlob(async (blob) => {
      const file = new File([blob], filename, { type: 'image/png' })
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: text || 'LINEBRKR NHL' })
          return resolve(true)
        }
      } catch (_) { /* fall through to download */ }
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob); a.download = filename; a.click()
      setTimeout(() => URL.revokeObjectURL(a.href), 2000)
      resolve(true)
    }, 'image/png')
  })
}

// ── fetch + render entry points ──────────────────────────────────────────────
export const sharePick = (p) =>
  shareCanvas(drawNhlPick(p), `linebrkr-nhl-${(p.name || 'pick').replace(/\s+/g, '-')}.png`,
    `${p.name} ${lineTxt(p)} — ${pct(p.prob_over)} · LINEBRKR NHL`)

export async function shareTop(market, date) {
  const r = await api.getNHLLeaderboard(market, date)
  const plays = (r.plays || []).filter(p => p.tier !== 'PASS').slice(0, 8)
  return shareCanvas(drawNhlTop(plays, market), `linebrkr-nhl-top-${market}.png`,
    `Top ${MARKET_LABEL[market] || market} — LINEBRKR NHL`)
}

export async function shareProof() {
  const r = await api.getNHLProof()
  return shareCanvas(drawNhlProof(r.scorecard || {}, r.tiers || {}),
    'linebrkr-nhl-record.png', 'LINEBRKR NHL — track record')
}

// ── "Share a board" entry points ─────────────────────────────────────────────
// Each throws a plain-English Error when there's nothing honest to post (the sheet
// shows it) rather than drawing an empty or padded graphic.
const fmtDay = d => { try { return new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) } catch (_) { return d } }
const TIER_ORDER = { ELITE: 0, 'BEST BET': 1, STRONG: 2, PLAY: 3, WATCH: 4, PASS: 5 }
const byTier = (a, b) => (TIER_ORDER[a.tier] ?? 9) - (TIER_ORDER[b.tier] ?? 9) || (b.prob_over || 0) - (a.prob_over || 0)

async function boardPlays(date, market) {
  const r = await api.getNHLLeaderboard(market || '', date, 250)
  if (r._sample) throw new Error("Couldn't load the board — try again.")
  return r.plays || []
}

export async function shareNhlTop10(date) {
  const plays = (await boardPlays(date)).filter(p => p.tier !== 'PASS').sort(byTier).slice(0, 10)
  if (!plays.length) throw new Error('No projections for this date yet.')
  return shareCanvas(drawNhlBoard('Top 10 Board', fmtDay(date), plays,
    'Ranked by model grade, then hit %. Edge only where a real book price exists.'),
    `linebrkr-nhl-top10-${date}.png`, 'Top 10 — LINEBRKR NHL')
}

export async function shareNhlBest(date) {
  const plays = (await boardPlays(date)).filter(p => ['ELITE', 'BEST BET', 'STRONG'].includes(p.tier)).sort(byTier)
  if (!plays.length) throw new Error('No best bets yet — a best bet needs a posted price the model beats. Lines post ~1:30 PM ET.')
  return shareCanvas(drawNhlBoard("Today's Best Bets", fmtDay(date), plays,
    'Model beats the book price · graded on the public record.'),
    `linebrkr-nhl-best-${date}.png`, 'Best bets — LINEBRKR NHL')
}

const MARKET_TITLE = { shots: 'Shots on Goal', goals: 'Light the Lamp', points: 'Points' }
export async function shareNhlMarket(market, date) {
  const plays = (await boardPlays(date, market)).filter(p => p.tier !== 'PASS')
    .sort((a, b) => (b.prob_over || 0) - (a.prob_over || 0)).slice(0, 10)
  if (!plays.length) throw new Error(`No ${MARKET_LABEL[market] || market} plays for this date yet.`)
  return shareCanvas(drawNhlBoard(MARKET_TITLE[market] || (MARKET_LABEL[market] || market), fmtDay(date), plays,
    market === 'goals' ? 'Chance to score 1+ goal · price = anytime goalscorer.' : 'Model hit % at the shown line.'),
    `linebrkr-nhl-${market}-${date}.png`, `${MARKET_TITLE[market] || market} — LINEBRKR NHL`)
}

export async function shareNhlSpots(date) {
  const sig = await api.getNHLSignals(date)
  const n = (sig.shootouts?.length || 0) + (sig.weak_pk?.length || 0) + (sig.goalie_spots?.length || 0) + (sig.back_to_backs?.length || 0)
  if (sig._sample || !n) throw new Error('No standout spots for this date.')
  return shareCanvas(drawNhlSpots(sig, fmtDay(date)), `linebrkr-nhl-spots-${date}.png`, 'Matchup spots — LINEBRKR NHL')
}

export async function shareNhlRecap(date) {
  const r = await api.getNHLResults(date, 'recommended')
  const card = (r.card || []).filter(x => x.result === 'win' || x.result === 'loss')
  if (r._sample || !card.length) throw new Error(`No graded best bets for ${fmtDay(date)}.`)
  return shareCanvas(drawNhlRecap(card, r.record, fmtDay(date)), `linebrkr-nhl-recap-${date}.png`, 'Results — LINEBRKR NHL')
}
